import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal, maskWholeNumber, productColors } from '@givebutter/util';
import {
    faArrowRightLong,
    faChevronLeft,
    faClone,
    faEyeSlash,
    faStar,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AddressReadable,
    copyAddress,
} from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import { ItemOptionsDropdown } from 'components/pages/campaign-settings/campaign-auction/ItemOptionsDropdown';
import { useAuctionCategories } from 'components/pages/campaign-settings/campaign-auction/auction-categories/useAuctionCategories';
import { Bids } from 'components/pages/campaign-settings/campaign-auction/item-details/bids';
import { useItemDetails } from 'components/pages/campaign-settings/campaign-auction/item-details/useItemDetails';
import {
    ReserveMet,
    StatusIndicator,
} from 'components/pages/campaign-settings/campaign-auction/items-index/useItemsIndex';
import { getFormattedItemStatus } from 'components/pages/campaign-settings/campaign-auction/util';
import { useCopy } from 'components/snippet';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { dateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { CampaignItem } from 'types/domain';

const DATETIME_FORMAT = {
    ...DateTime.DATETIME_FULL,
    timeZoneName: undefined,
};

const ItemDetails = () => {
    const { item, campaign, itemRoutes, onDeleteItem } = useItemDetails();
    const { auctionsItemStatus } = useFlags();
    const { data: categories } = useAuctionCategories({
        campaignId: campaign.id,
        auctionId: item.auction_id,
    });
    const category = categories?.find(cat => cat.id === item.category_id);

    const currencySymbol = useCurrencySymbol();
    const dateRange = [
        dateFromServer(item.start_at ?? '').toLocaleString(DATETIME_FORMAT),
        dateFromServer(item.end_at ?? '').toLocaleString(DATETIME_FORMAT),
    ];
    const [mainImg, ...images] = item.pictures?.map(media => media.url) ?? [];
    const [mainSponsorImg, ...sponsorImages] =
        item.sponsor_item?.media?.map(media => media.url) ?? [];
    const showBuyNowPrice = coerceNullNumberToBool(item.price) && item.price! > 0;
    const sponsor = item.sponsor_item?.sponsor;

    return (
        <div className={'flex flex-col gap-y-8'}>
            <Link className={'w-fit'} to={itemRoutes.index()}>
                <Button variant="outline" color="neutral" size="sm">
                    <FontAwesomeIcon size={'sm'} icon={faChevronLeft} />
                    Back to items
                </Button>
            </Link>
            {item.is_published && <Bids />}
            <Box className="flex flex-col gap-y-8 p-8">
                <div className={'flex items-center justify-between'}>
                    <div className={'flex flex-col gap-y-1'}>
                        <p className={'text-sm text-gray-500'}>#{item.id}</p>
                        <h2 className={'text-2xl font-medium'}>{item.name}</h2>
                        <div className={'flex items-center gap-x-2'}>
                            {auctionsItemStatus && (
                                <div className={'flex items-center gap-x-2'}>
                                    <StatusIndicator status={item.dashboard_status} />
                                    <p>{getFormattedItemStatus(item.dashboard_status)}</p>
                                </div>
                            )}
                            {item.in_person && <Badge>In-person</Badge>}
                            {item.visibility === 'private' && (
                                <div
                                    className={
                                        'flex items-center gap-x-2 rounded-full bg-gray-100 px-2'
                                    }
                                >
                                    <FontAwesomeIcon icon={faEyeSlash} />
                                    <p>Private</p>
                                </div>
                            )}
                            {item.visibility === 'featured' && (
                                <div
                                    className={
                                        'flex items-center gap-x-2 rounded-full bg-gray-100 px-2'
                                    }
                                >
                                    <p>Featured</p>
                                </div>
                            )}
                            {category && (
                                <div
                                    className={
                                        'flex items-center gap-x-2 rounded-full bg-gray-100 px-2'
                                    }
                                >
                                    <p>
                                        {category.icon ? `${category.icon} ` : ''}
                                        {category.name}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <ItemOptionsDropdown item={item} onDelete={onDeleteItem} />
                </div>
                <WinningBidder item={item} />
                <div className={'flex flex-col gap-y-8'}>
                    {!!sponsor && (
                        <div className={'flex flex-row items-center gap-x-1'}>
                            <span>Sponsored by</span>
                            {!!sponsor?.logo_url && (
                                <img
                                    src={sponsor?.logo_url}
                                    alt={sponsor?.name}
                                    className="max-h-6 w-auto"
                                />
                            )}
                            {!sponsor?.logo_url && <span>{sponsor?.name}</span>}
                        </div>
                    )}
                    {(!!item.description || !!item.sponsor_item?.description) && (
                        <div className={'flex flex-col gap-y-4'}>
                            <p className={'text-base font-medium'}>Description</p>
                            {item.description && (
                                <givebutter-html-renderer
                                    html={item.description}
                                ></givebutter-html-renderer>
                            )}

                            {item.sponsor_item?.description &&
                                !item.sponsor_item_config?.hide_description && (
                                    <givebutter-html-renderer
                                        html={item.sponsor_item.description}
                                    ></givebutter-html-renderer>
                                )}
                        </div>
                    )}
                    <div className={'grid grid-cols-2 gap-8 md:grid-cols-3'}>
                        {!!item.allows_bid && coerceNullNumberToBool(item.start_bid) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Starting price'}
                                    value={`${currencySymbol}${maskDecimal(item.start_bid ?? '')}`}
                                />
                            </div>
                        )}
                        {!!item.allows_bid && coerceNullNumberToBool(item.reserve_price) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Reserve price'}
                                    value={`${currencySymbol}${maskDecimal(
                                        item.reserve_price ?? '',
                                    )}`}
                                />
                            </div>
                        )}
                        {showBuyNowPrice && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Buy it now'}
                                    value={`${currencySymbol}${maskDecimal(item.price ?? '')}`}
                                />
                            </div>
                        )}
                        {!!item.allows_bid && coerceNullNumberToBool(item.bid_increment) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Bid increment'}
                                    value={`${currencySymbol}${maskDecimal(
                                        item.bid_increment ?? '',
                                    )}`}
                                />
                            </div>
                        )}
                        {coerceNullNumberToBool(item.retail_price) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Retail value'}
                                    value={`${currencySymbol}${maskDecimal(
                                        item.retail_price ?? '',
                                    )}`}
                                />
                            </div>
                        )}
                        {!item.allows_bid && coerceNullNumberToBool(item.total_quantity) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Quantity'}
                                    value={`${maskWholeNumber(item.total_quantity)}`}
                                />
                            </div>
                        )}
                        {item.require_shipping && coerceNullNumberToBool(item.shipping_price) && (
                            <div className={'col-span-1'}>
                                <DataPoint
                                    label={'Shipping Price'}
                                    value={`${currencySymbol}${maskDecimal(
                                        item.shipping_price ?? '',
                                    )}`}
                                />
                            </div>
                        )}
                    </div>
                    {(item.pictures?.length ||
                        (!item.sponsor_item_config?.hide_media &&
                            item.sponsor_item?.media?.length)) && (
                        <div className={'grid grid-cols-2 gap-4'}>
                            <div
                                className={
                                    'relative col-span-1 h-[200px] rounded bg-gray-300 bg-contain bg-local bg-center bg-no-repeat'
                                }
                                style={{ backgroundImage: `url(${mainImg || mainSponsorImg})` }}
                            >
                                <div
                                    className={
                                        'absolute left-2 top-2 flex items-center gap-x-2 rounded-full bg-orange-100 px-2 py-1 text-orange-400'
                                    }
                                >
                                    <FontAwesomeIcon icon={faStar} size={'sm'} />
                                    <p>Main Image</p>
                                </div>
                            </div>
                            <div
                                className={`col-span-1 grid h-[200px] grid-cols-3 gap-4 overflow-auto`}
                            >
                                {images.map(img => (
                                    <div
                                        key={img}
                                        className={
                                            'relative h-[88px] rounded bg-gray-300 bg-contain bg-local bg-center bg-no-repeat'
                                        }
                                        style={{ backgroundImage: `url(${img})` }}
                                    />
                                ))}
                                {sponsorImages.map(img => (
                                    <div
                                        key={img}
                                        className={
                                            'relative h-[88px] rounded bg-gray-300 bg-contain bg-local bg-center bg-no-repeat'
                                        }
                                        style={{ backgroundImage: `url(${img})` }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {!!dateRange && (
                        <div className={'flex flex-col gap-y-4'}>
                            <p className={'text-base font-medium'}>Bidding date &amp; time</p>
                            <div className={'flex items-center gap-x-4'}>
                                <p className={'text-xl font-medium'}>{dateRange[0]}</p>
                                <FontAwesomeIcon icon={faArrowRightLong} />
                                <p className={'text-xl font-medium'}>{dateRange[1]}</p>
                            </div>
                        </div>
                    )}
                    {!!item.purchase_message && (
                        <div className={'flex flex-col gap-y-4'}>
                            <p className={'text-base font-medium'}>Fulfillment details</p>
                            <givebutter-html-renderer
                                html={item.purchase_message}
                            ></givebutter-html-renderer>
                        </div>
                    )}
                    {!item.sponsor_item_config?.hide_purchase_message &&
                        item.sponsor_item?.purchase_message && (
                            <div className={'flex flex-col gap-y-4'}>
                                <p className={'text-base font-medium'}>
                                    Purchase Message provided by {item.sponsor_item.sponsor?.name}
                                </p>
                                <givebutter-html-renderer
                                    html={item.sponsor_item.purchase_message}
                                ></givebutter-html-renderer>
                            </div>
                        )}
                </div>
            </Box>
        </div>
    );
};

const DataPoint = ({ label, value }: { label: string; value: string }) => {
    return (
        <div className={'flex flex-col gap-y-2'}>
            <p className={'text-base font-medium'}>{label}</p>
            <p className={'text-xl font-medium'}>{value}</p>
        </div>
    );
};

const CopyContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.75rem;
    border-radius: 6px;
    background: ${productColors.gray[50]};
    border: 1px solid ${productColors.gray[200]};
`;

const WinningBidder = ({ item }: { item: CampaignItem }) => {
    const { onClickCopy } = useCopy();

    if (!item.highest_bid?.bidder || item.dashboard_status !== 'sold') {
        return <></>;
    }

    const bidder = item.highest_bid.bidder;

    const onCopyEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClickCopy(e, bidder.email ?? '');
    };

    const onCopyPhone = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClickCopy(e, bidder.phone ?? '');
    };

    const onCopyAddress = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClickCopy(e, bidder.address ? copyAddress(bidder.address) : '');
    };

    return (
        <div className={'flex flex-col'}>
            <div>
                <div>
                    <p>Winning Bidder</p>
                    <p>
                        {bidder.first_name} {bidder.last_name}
                    </p>
                </div>
                <div>
                    <p>ID #</p>
                    <p>{bidder.number}</p>
                </div>

                <div>
                    <p>Sold Price</p>
                    <p>
                        {maskDecimal(item?.highest_bid?.transaction_item?.total ?? '')}
                        <ReserveMet item={item} />
                    </p>
                </div>
            </div>

            <div>
                <div>
                    <div>
                        <div>Contact</div>
                        {!!bidder.email && (
                            <CopyContainer>
                                <p>{bidder.email}</p>
                                <Button
                                    variant="solid"
                                    onClick={onCopyEmail}
                                    shape={'square'}
                                    aria-label="Copy email"
                                >
                                    <FontAwesomeIcon icon={faClone} />
                                </Button>
                            </CopyContainer>
                        )}
                        {!!bidder.phone && (
                            <CopyContainer>
                                <p>{bidder.phone}</p>
                                <Button
                                    variant="solid"
                                    onClick={onCopyPhone}
                                    shape={'square'}
                                    aria-label="Copy phone"
                                >
                                    <FontAwesomeIcon icon={faClone} />
                                </Button>
                            </CopyContainer>
                        )}
                    </div>
                </div>
                {!!bidder.address && (
                    <div>
                        <p>Shipping Address</p>
                        <CopyContainer>
                            <AddressReadable address={bidder.address} />
                            <Button
                                variant="solid"
                                onClick={onCopyAddress}
                                shape={'square'}
                                aria-label="Copy address"
                            >
                                <FontAwesomeIcon icon={faClone} />
                            </Button>
                        </CopyContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

const coerceNullNumberToBool = (prop?: Number | null) => {
    return typeof prop === 'number';
};

export { ItemDetails };
